import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { usePanel } from 'contexts/panelContext';
import { useAuth } from 'contexts/authContext';
import { Loader } from 'components/_new/Loader';
import { EmptyState } from 'utils/emptyState';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { ArticleList } from './ArticleList';

type DataWrapperProps = {
	renderData?: (data: KnowledgeBaseArticleContentItem[]) => ReactNode;
	// eslint-disable-next-line react/no-unused-prop-types
	disableRating?: boolean;
};

export const DirectoryResults = ({ renderData }: DataWrapperProps) => {
	const { currentUser } = useAuth();
	const {
		context,
		searchValue,
		pathParams: { directory },
		queries: { getArticlesByDirectory },
	} = useKnowledgeBase();

	const {
		data: articleListDirectory,
		isLoading: articleListDirectoryLoading,
		isFetched: articleListDirectoryFetched,
	} = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE_DIRECTORY_LIST',
			{
				context,
				directory: directory?.id,
				language: currentUser?.language,
				searchValue,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => getArticlesByDirectory(Number(directory?.id), searchValue),
		{
			enabled: Boolean(directory),
		}
	);

	if (directory) {
		if (articleListDirectoryLoading) {
			return <Loader />;
		}

		if (articleListDirectoryFetched) {
			if (!articleListDirectory?.length) {
				return <EmptyState />;
			}

			if (renderData) {
				return <>{renderData(articleListDirectory)}</>;
			}

			return <ArticleList items={articleListDirectory} variant="card" />;
		}
	}

	return null;
};

export const TagResults = ({ renderData }: DataWrapperProps) => {
	const { currentUser } = useAuth();
	const {
		context,
		searchValue,
		pathParams: { tag },
		queries: { getArticlesByTag },
	} = useKnowledgeBase();

	const {
		data: articleListTag,
		isLoading: articleListTagLoading,
		isFetched: articleListTagFetched,
	} = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE_TAG_LIST',
			{
				context,
				directory: tag?.id,
				language: currentUser?.language,
				searchValue,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => getArticlesByTag(Number(tag?.id), searchValue),
		{
			enabled: Boolean(tag),
		}
	);

	if (tag) {
		if (articleListTagLoading) {
			return <Loader />;
		}

		if (articleListTagFetched) {
			if (!articleListTag?.length) {
				return <EmptyState />;
			}

			if (renderData) {
				return <>{renderData(articleListTag)}</>;
			}

			return <ArticleList items={articleListTag} variant="card" />;
		}
	}

	return null;
};

export const SearchResults = ({ renderData, disableRating = false }: DataWrapperProps) => {
	const { currentUser } = useAuth();
	const { context, searchValue, queries } = useKnowledgeBase();

	const enabled = Boolean(searchValue) && String(searchValue).length > 0;
	const {
		data: articleListSearch,
		isLoading: articleListSearchLoading,
		isFetched: articleListSearchFetched,
	} = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE_SEARCH_LIST',
			{
				context,
				searchValue,
				language: currentUser?.language,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => queries.getArticlesBySearch(String(searchValue)),
		{
			enabled,
		}
	);

	if (enabled) {
		if (articleListSearchLoading) {
			return <Loader />;
		}

		if (articleListSearchFetched) {
			if (!articleListSearch?.length) {
				return <EmptyState />;
			}

			if (renderData) {
				return <>{renderData(articleListSearch)}</>;
			}

			return <ArticleList items={articleListSearch} variant="card" />;
		}
	}

	return null;
};

export const FlagResults = ({ renderData, flag }: DataWrapperProps & { flag: 'favorite' | 'toread' | 'readed' }) => {
	const { t } = useTranslation();

	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();

	const pageTitle = (() => {
		switch (flag) {
			case 'favorite':
				return t('knowledgeBase.favorite.favorite');
			case 'toread':
				return t('knowledgeBase.toRead.toRead');
			case 'readed':
				return t('knowledgeBase.readed.readed');
			default:
				return '';
		}
	})();
	const { context } = useKnowledgeBase({ breadcrums: [{ label: pageTitle }] });

	const {
		data: articleList,
		isLoading: articleListLoading,
		isFetched: articleListFetched,
	} = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE_FLAG_LIST',
			{
				context,
				applicationId: Number(currentApplicationData?.id),
				language: currentUser?.language,
				flag,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => knowledgeBaseApi.application.getArticlesByFlag(Number(currentApplicationData?.id), flag),
		{
			enabled: true,
		}
	);

	if (articleListLoading) {
		return <Loader />;
	}

	if (articleListFetched) {
		if (!articleList?.length) {
			return <EmptyState />;
		}

		if (renderData) {
			return <>{renderData(articleList)}</>;
		}

		return <ArticleList items={articleList} variant="card" />;
	}

	return null;
};

export const Results = ({ renderData }: DataWrapperProps) => {
	const { isMainPage, isArticlePage, isSearch, pathParams } = useKnowledgeBase();
	return (
		<div className="flex-1 overflow-auto">
			{!isMainPage && !isArticlePage && <DirectoryResults renderData={renderData} />}
			{!isMainPage && !isArticlePage && <TagResults renderData={renderData} />}
			{isSearch && !isArticlePage && !pathParams.directory && !pathParams.tag && (
				<SearchResults renderData={renderData} disableRating />
			)}
		</div>
	);
};
